import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem } from '@mui/material';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import useAuth from 'src/hooks/useAuth';
import { useNavigate } from 'react-router';
import SvgUser from 'src/components/icons/24/outlined/other/person/User';
import useImpersonateContext from 'src/hooks/useImpersonateContext';
import ImpersonateDialog from './ImpersonateDialog';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  { label: 'Change password', linkTo: '/dashboard/profile/change-password' },
  { label: 'Profile', linkTo: '/dashboard/profile/profile' },
];

// ----------------------------------------------------------------------

type IProps = {
  size?: any;
  color?: string;
};

export default function AccountPopover(props: IProps) {
  const { size, color } = props;

  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const { onOpen, returning, onReturnToOriginal } = useImpersonateContext();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  // Impersonation state derived from /me response
  const impersonation = user?.['impersonation'];
  const isAlreadyImpersonating = !!impersonation;

  // Show "Impersonate user" only if the customer has the feature enabled
  // and we are NOT already in an impersonation session (no nesting)
  const canImpersonate =
    !!user?.['permission']?.['impersonation']?.some(
      (p: any) => p.action === 'IMPERSONATION'
    ) && !isAlreadyImpersonating;

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setOpen(event.currentTarget);
  const handleClose = () => setOpen(null);

  const handleAccountPopover = (link: string) => {
    handleClose();
    navigate(link);
  };

  const handleOpenImpersonate = () => {
    handleClose();
    onOpen();
  };

  const handleReturnToOriginal = () => {
    handleClose();
    onReturnToOriginal();
  };

  return (
    <>
      <IconButtonAnimate onClick={handleOpen} sx={{ width: 40, height: 40, p: 0 }}>
        <SvgUser color={color} size={size ? size : 'medium'} />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          width: 250,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >

        {!isAlreadyImpersonating && (
          <Box sx={{ my: 1.5, px: 2.5 }}>
            <Typography variant="body1_14" noWrap>
              {user?.['name']} {user?.['surname']}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
              {user?.['username']}
            </Typography>
          </Box>
        )}

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {/* Hide Change password and Profile during impersonation */}
          {!isAlreadyImpersonating && MENU_OPTIONS.map((option) => (
            <MenuItem key={option.label} onClick={() => handleAccountPopover(option.linkTo)}>
              {option.label}
            </MenuItem>
          ))}

          {/* Impersonate user — only for authorized users not already impersonating */}
          {canImpersonate && (
            <MenuItem onClick={handleOpenImpersonate}>
              Show dashboard as user
            </MenuItem>
          )}

          {/* Return to original account — only during impersonation */}
          {isAlreadyImpersonating && (
            <MenuItem
              onClick={handleReturnToOriginal}
              disabled={returning}
            >
              Return to {impersonation.originalUserName?.toLowerCase() ?? 'your account'}
            </MenuItem>
          )}
        </Stack>
      </MenuPopover>

      {/* Dialog rendered outside the popover to avoid clipping */}
      <ImpersonateDialog />
    </>
  );
}